/**
 * Browser-side consent helpers. Reads/writes the first-party consent cookie
 * and applies the choice to PostHog (opt-in/out, persistence, session
 * recording). Import only from client code.
 */

import posthog from 'posthog-js'
import { isPostHogCaptureEnabled } from '@lib/posthog-enabled'
import { logger } from '@lib/logger'
import {
  CONSENT_COOKIE,
  CONSENT_MAX_AGE_SECONDS,
  CONSENT_VERSION,
  consentFromCookieString,
  serializeConsent,
  type ConsentState,
} from './consent'

/** Dispatched on `window` whenever the user changes their consent choice. */
export const CONSENT_CHANGE_EVENT = 'lamaven:consent-change'

export function readConsent(): ConsentState | null {
  if (typeof document === 'undefined') return null
  return consentFromCookieString(document.cookie)
}

export function isGpcEnabled(): boolean {
  if (typeof navigator === 'undefined') return false
  return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true
}

/**
 * Persist a consent choice, apply it to PostHog, and notify listeners
 * so gated scripts mount/unmount without a reload.
 */
export function writeConsent(analytics: boolean): ConsentState {
  // A Global Privacy Control signal is a binding opt-out (CCPA/CPRA):
  // analytics can never be enabled while it is asserted.
  const gpc = isGpcEnabled()
  const state: ConsentState = {
    v: CONSENT_VERSION,
    essential: true,
    analytics: analytics && !gpc,
    ts: new Date().toISOString(),
    ...(gpc ? { gpc: true } : {}),
  }
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(serializeConsent(state))}; Path=/; Max-Age=${CONSENT_MAX_AGE_SECONDS}; SameSite=Lax${secure}`
  applyConsentToPostHog(state)
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: state }))
  return state
}

/**
 * Apply a consent state to the already-initialized PostHog instance.
 * On grant: cookie persistence + capturing + masked session recording.
 * On refusal/withdrawal: stop everything and remove `ph_*` artifacts.
 * No-op on localhost (SDK is not initialized there).
 */
export function applyConsentToPostHog(state: ConsentState | null): void {
  if (typeof window === 'undefined') return
  if (!isPostHogCaptureEnabled()) return
  if (state?.analytics === true) {
    posthog.set_config({ persistence: 'localStorage+cookie' })
    posthog.opt_in_capturing({ captureEventName: null })
    posthog.startSessionRecording()
  } else {
    posthog.stopSessionRecording()
    posthog.opt_out_capturing()
    posthog.set_config({ persistence: 'memory' })
    clearPostHogArtifacts()
  }
}

/** Remove PostHog cookies/localStorage left behind by a prior opt-in. */
function clearPostHogArtifacts(): void {
  for (const part of document.cookie.split('; ')) {
    const name = part.split('=')[0]
    if (name.startsWith('ph_')) {
      document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`
      document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax; Domain=${window.location.hostname}`
    }
  }
  try {
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i)
      if (key?.startsWith('ph_')) window.localStorage.removeItem(key)
    }
  } catch (err) {
    // localStorage unavailable (private mode/quota) — nothing to clear
    logger.warn('consent', 'localStorage unavailable while clearing PostHog artifacts', { error: err instanceof Error ? err.message : String(err) })
  }
}
